import React, { useState, useEffect } from 'react';
import axios from '../utils/axios';
import LeadForm from './LeadForm';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';

const BranchLeadManagement = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [branchInfo, setBranchInfo] = useState(null);

  useEffect(() => {
    const storedBranchInfo = localStorage.getItem('branchInfo');
    if (storedBranchInfo) {
      setBranchInfo(JSON.parse(storedBranchInfo));
    }
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('branchToken');
      const response = await axios.get('/branch-auth/leads', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      console.log('✅ Branch leads loaded:', response.data.data.length);
      setLeads(response.data.data || []);
    } catch (error) {
      console.error('Error fetching branch leads:', error);
      setLeads([]);
    }
    setLoading(false);
  };

  const handleAddLead = async (leadData) => {
    try {
      const token = localStorage.getItem('branchToken');
      
      // Add branch ID to lead data
      const branchLeadData = {
        ...leadData,
        assignedBranch: branchInfo._id,
        company: branchInfo.company
      };

      await axios.post('/leads', branchLeadData, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      await fetchLeads();
      setShowAddModal(false);
      alert('✅ Lead added successfully!');
    } catch (error) {
      console.error('Error adding lead:', error);
      alert('❌ Failed to add lead: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleEditLead = async (leadData) => {
    try {
      const token = localStorage.getItem('branchToken');
      
      await axios.put(`/leads/${editingLead._id}`, leadData, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      await fetchLeads();
      setEditingLead(null);
      setShowAddModal(false);
      alert('✅ Lead updated successfully!');
    } catch (error) {
      console.error('Error updating lead:', error);
      alert('❌ Failed to update lead: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleDeleteLead = async (leadId) => {
    if (!window.confirm('Are you sure you want to delete this lead?')) return;

    try {
      const token = localStorage.getItem('branchToken');
      
      await axios.delete(`/leads/${leadId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      await fetchLeads();
      alert('✅ Lead deleted successfully!');
    } catch (error) {
      console.error('Error deleting lead:', error);
      alert('❌ Failed to delete lead: ' + (error.response?.data?.message || error.message));
    }
  };

  const filteredLeads = leads.filter(lead => {
    const matchesSearch = 
      lead.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.contactNumber?.includes(searchTerm) ||
      lead.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || lead.status === filterStatus;
    
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Branch Leads</h2>
            <p className="text-gray-600 text-sm mt-1">
              Manage leads for {branchInfo?.name}
            </p>
          </div>
          <button
            onClick={() => {
              setEditingLead(null);
              setShowAddModal(true);
            }}
            className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
          >
            <PlusIcon className="h-5 w-5" />
            Add Lead
          </button>
        </div>

        {/* Search and Filter */}
        <div className="flex gap-4">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              placeholder="Search leads..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <div className="relative">
            <FunnelIcon className="h-5 w-5 absolute left-3 top-3 text-gray-400" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="pl-10 pr-8 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="all">All Status</option>
              <option value="new">New</option>
              <option value="contacted">Contacted</option>
              <option value="qualified">Qualified</option>
              <option value="converted">Converted</option>
              <option value="lost">Lost</option>
            </select>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-blue-500">
          <p className="text-gray-500 text-sm">Total Leads</p>
          <p className="text-2xl font-bold text-gray-900">{leads.length}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-green-500">
          <p className="text-gray-500 text-sm">New</p>
          <p className="text-2xl font-bold text-gray-900">
            {leads.filter(l => l.status === 'new').length}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-yellow-500">
          <p className="text-gray-500 text-sm">Qualified</p>
          <p className="text-2xl font-bold text-gray-900">
            {leads.filter(l => l.status === 'qualified').length}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-purple-500">
          <p className="text-gray-500 text-sm">Converted</p>
          <p className="text-2xl font-bold text-gray-900">
            {leads.filter(l => l.status === 'converted').length}
          </p>
        </div>
      </div>

      {/* Leads Table */}
      {filteredLeads.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No leads found</h3>
          <p className="text-gray-500 mb-4">
            {searchTerm || filterStatus !== 'all' 
              ? 'Try adjusting your search or filters' 
              : 'Get started by adding your first lead'}
          </p>
          {!searchTerm && filterStatus === 'all' && (
            <button
              onClick={() => setShowAddModal(true)}
              className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg inline-flex items-center gap-2"
            >
              <PlusIcon className="h-5 w-5" />
              Add First Lead
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Product Interest
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredLeads.map((lead) => (
                  <tr key={lead._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{lead.customerName}</div>
                      <div className="text-sm text-gray-500">{lead.customerCompany}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{lead.contactNumber}</div>
                      <div className="text-sm text-gray-500">{lead.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {lead.productOfInterest || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        lead.status === 'new' ? 'bg-blue-100 text-blue-800' :
                        lead.status === 'contacted' ? 'bg-yellow-100 text-yellow-800' :
                        lead.status === 'qualified' ? 'bg-green-100 text-green-800' :
                        lead.status === 'converted' ? 'bg-purple-100 text-purple-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {lead.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => {
                          setEditingLead(lead);
                          setShowAddModal(true);
                        }}
                        className="text-purple-600 hover:text-purple-900 mr-3"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteLead(lead._id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add/Edit Lead Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingLead ? 'Edit Lead' : 'Add New Lead'}
              </h3>
            </div>
            <LeadForm
              lead={editingLead}
              onSubmit={editingLead ? handleEditLead : handleAddLead}
              onCancel={() => {
                setShowAddModal(false);
                setEditingLead(null);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default BranchLeadManagement;
